// Browser-session telemetry for the exact symptom reported in production:
// "the first generations obey, then after 4-5 the success ratio drops".
//
// This never changes the ARK payload: JobCreate.client_context is persisted by
// Orca but is not forwarded upstream. The interceptor only annotates actual
// generation-spending requests so historical jobs can be bucketed by position
// within a browser session and within one source shot.

let installed = false;
let sessionOrdinal = 0;
let shotOrdinal = 0;
let lastShotScope = "workspace";
const sessionId = (() => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_e) { /* fallback below */ }
  return `ui_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
})();

function submitKind(config) {
  if (String(config?.method || "get").toLowerCase() !== "post") return null;
  const url = String(config?.url || "").split("?")[0].replace(/\/$/, "");
  if (url.endsWith("/api/generations") || url.endsWith("/generations")) return "direct";
  if (url.endsWith("/api/sd25-control/execute") || url.endsWith("/sd25-control/execute")) return "controlled";
  return null;
}

function referenceCount(data) {
  const imageRefs = Array.isArray(data?.reference_images) ? data.reference_images.length : 0;
  const assetRefs = Array.isArray(data?.asset_refs) ? data.asset_refs.length : 0;
  const fixedImages = [
    data?.image_ref_storage_path || data?.image_ref_url || data?.image_ref_asset_id,
    data?.first_frame_storage_path || data?.first_frame_url || data?.first_frame_asset_id,
    data?.last_frame_storage_path || data?.last_frame_url || data?.last_frame_asset_id,
  ].filter(Boolean).length;
  const source = data?.video_storage_path || data?.video_url || data?.video_asset_id ? 1 : 0;
  return imageRefs + assetRefs + fixedImages + source;
}

function annotatedPayload(data, scope) {
  sessionOrdinal += 1;
  shotOrdinal += 1;
  return {
    ...data,
    client_context: {
      ...(data?.client_context || {}),
      ui_session_id: sessionId,
      ui_session_ordinal: sessionOrdinal,
      ui_shot_scope: scope,
      ui_shot_ordinal: shotOrdinal,
      ui_prompt_chars: String(data?.prompt || "").length,
      ui_reference_count: referenceCount(data),
    },
  };
}

export function installGenerationSessionTelemetry(axiosInstance, getShotScope = () => "workspace") {
  if (installed || !axiosInstance?.interceptors?.request) return;
  installed = true;
  axiosInstance.interceptors.request.use((config) => {
    const kind = submitKind(config);
    if (!kind) return config;

    const scope = getShotScope() || "workspace";
    if (scope !== lastShotScope) {
      lastShotScope = scope;
      shotOrdinal = 0;
    }

    if (kind === "controlled") {
      // Production Control rewrites /generations -> /sd25-control/execute and
      // nests the original JobCreate under `payload`. This interceptor is
      // installed BEFORE Production Control (index.js), so Axios executes the
      // later control interceptor first. Suppressed legacy Variants throw there
      // and never reach this point; therefore ordinals count real spends, not
      // discarded UI copies.
      const envelope = (config.data && typeof config.data === "object") ? config.data : {};
      const payload = (envelope.payload && typeof envelope.payload === "object") ? envelope.payload : {};
      config.data = { ...envelope, payload: annotatedPayload(payload, scope) };
      return config;
    }

    const data = (config.data && typeof config.data === "object") ? config.data : {};
    config.data = annotatedPayload(data, scope);
    return config;
  });
}

// Pure helpers exported for isolated guards/tests.
export const _test = { submitKind, referenceCount };
